import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, TextField, Button, CircularProgress,
  Paper, IconButton, Tooltip, Chip, Divider, Snackbar, Alert, Drawer,
  List, ListItem, ListItemText, ListItemIcon, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem,
  Collapse, Badge, ListSubheader, Grid, Fab, Popover, useTheme, useMediaQuery
} from '@mui/material';
import {
  Send as SendIcon,
  VolumeUp as VolumeUpIcon,
  Translate as TranslateIcon,
  Favorite as FavoriteIcon,
  ArrowBack as ArrowBackIcon,
  ListAlt as ListAltIcon,
  Help as HelpIcon,
  SentimentSatisfied as SmileyIcon,
  SentimentDissatisfied as SadIcon,
  MenuBook as MenuBookIcon,
  Close as CloseIcon,
  FormatQuote as QuoteIcon,
  School as SchoolIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/apiClient';
import ResponseSuggestions from "../components/CollapsibleResponseOptions";
import ConversationActions, { CONVERSATION_ACTIONS } from "../components/ConversationActions";
import ConversationModeOverlay from "../components/ConversationModeOverlay";
import WordSuggestions from "../components/WordSuggestions";
import useWhisperRecorder from "../hooks/useWhisperRecorder";
import useAudioPlayer from "../hooks/useAudioPlayer";
import AudioPlayer from "../components/audioPlayer";
import SpeechRecognition from '../components/SpeechRecognition';
import { useSettings } from '../context/SettingsContext';
import { SpeechSettingsButton } from '../components/SpeechSettings';
import InlineSpeechRecognition from "../components/InlineSpeechRecognition";
import WhisperRecording from "../components/WhisperRecording";
import InlineAudioPlayer from "../components/InlineAudioPlayer";
import WordDialog from '../components/WordDialog';
import MessageBubble from '../components/MessageBubble';
import { CompactVoiceSelector } from '../components/VoiceSelector';
import { RecordVoiceOver as RecordVoiceOverIcon } from '@mui/icons-material';
import { useTutorContext } from '../context/TutorContext';

// Knowledge level options for vocabulary
const knowledgeLevels = [
  { value: 1, label: "Don't Know", color: 'error', icon: <SadIcon /> },
  { value: 2, label: 'Learning', color: 'warning', icon: <HelpIcon /> },
  { value: 3, label: 'Know Well', color: 'success', icon: <SmileyIcon /> }
];

const unescapeHTML = (html) => {
      if (!html) return '';
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.documentElement.textContent;
    };

const LearningSessionPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const messagesEndRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [microphoneActive, setMicrophoneActive] = useState(false);

  // Session state
  const [session, setSession] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);

  // Input state
  const [userInput, setUserInput] = useState('');

  // Translation state
  const [showTranslation, setShowTranslation] = useState(false);
  const [currentTranslation, setCurrentTranslation] = useState('');

  // Vocabulary drawer state
  const [vocabDrawerOpen, setVocabDrawerOpen] = useState(false);
  const [sessionVocabulary, setSessionVocabulary] = useState([]);
  const [loadingVocabulary, setLoadingVocabulary] = useState(false);

  // Word dialog state
  const [wordDialogOpen, setWordDialogOpen] = useState(false);
  const [selectedWord, setSelectedWord] = useState(null);

  const [currentAudioId, setCurrentAudioId] = useState(null);

  const [voiceGender, setVoiceGender] = useState('Female');
  const voiceQuality = 'low';

  // Publish page context to the floating tutor so it can give relevant help.
  const { setTutorContext, clearTutorContext, registerInputInsertHandler, clearInputInsertHandler } = useTutorContext();

  // Let the floating tutor drop a chosen suggested response into this chat input.
  useEffect(() => {
    registerInputInsertHandler((text) => setUserInput(text || ''));
    return () => clearInputInsertHandler();
  }, [registerInputInsertHandler, clearInputInsertHandler]);

  // Inside the LearningSessionPage component, add this state variable for the Whisper component
  const [whisperRecording, setWhisperRecording] = useState(false);

  // Word suggestions the learner hid (keyed by message id).
  const [dismissedSuggestions, setDismissedSuggestions] = useState({});

  // --- Hands-free "call" mode (#1) ---
  const [conversationMode, setConversationMode] = useState(false);
  const [loopState, setLoopState] = useState('idle'); // idle | listening | thinking | speaking | paused
  const [cmCurrentText, setCmCurrentText] = useState('');
  const [cmIsPlaying, setCmIsPlaying] = useState(false);   // TTS play/pause state
  const [cmRate, setCmRate] = useState(1);                 // TTS playback speed
  const conversationModeRef = useRef(false);   // synchronous "still in mode?" guard
  const conversationAudioRef = useRef(null);    // single, gesture-unlocked <audio> element
  const cmRateRef = useRef(1);                   // latest speed for the loop's plays
  // Custom "Other — set the scene" dialog state.
  const [customSceneOpen, setCustomSceneOpen] = useState(false);
  const [customSceneText, setCustomSceneText] = useState('');
  // Quick translation bubble (shown on word tap while in call mode).
  const [quickTrans, setQuickTrans] = useState(null); // { anchorEl, word, translation }
  // 0-sample silent WAV used to unlock audio playback inside the start gesture (iOS).
  const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=';
  // Call-mode mic auto-stop: send after a 2.5s pause once the learner has spoken
  // (learners hesitate, so shorter cuts them off), hard-cap a turn at 30s, and
  // turn the mic off if nothing is said for 10s instead of listening forever.
  const CM_SILENCE_MS = 2500;
  const CM_MAX_TURN_MS = 30000;
  const CM_NO_SPEECH_MS = 10000;
  // Recorder for the loop (separate from the WhisperRecording button in the input bar).
  const cmRecorder = useWhisperRecorder({
    sessionId,
    targetLanguage: session?.target_language,
    onResult: (text, audioId) => onRecorderResult(text, audioId),
    // Pause rather than re-open the mic: retrying on a denied mic would loop forever.
    onError: () => { if (conversationModeRef.current) setLoopState('paused'); },
    onAutoStop: (reason) => handleRecorderAutoStop(reason),
    maxDurationMs: CM_MAX_TURN_MS,
    silenceMs: CM_SILENCE_MS,
    noSpeechMs: CM_NO_SPEECH_MS,
  });

  // Add a handler for whisper recording state changes
  const handleWhisperRecordingChange = (isRecording) => {
    setWhisperRecording(isRecording);
  };

   // State for inline audio players - COMMENTED OUT FOR REFERENCE
  // const [inlineAudioPlayers, setInlineAudioPlayers] = useState(new Map());
  const [audioUrls, setAudioUrls] = useState(new Map()); // Cache for audio URLs

  // Get or create audio URL and cache it
  const getAudioUrl = (messageContent) => {
    const jobId = audioJobs[messageContent];
    if (!jobId) return null;

    // Check if we already have this URL cached
    const cacheKey = `${messageContent}-${jobId}`;
    if (audioUrls.has(cacheKey)) {
      return audioUrls.get(cacheKey);
    }

    // Create new URL and cache it
    const url = apiClient.audio.getAudioUrl(jobId);
    setAudioUrls(prev => new Map(prev.set(cacheKey, url)));
    return url;
  };

  // COMMENTED OUT: Toggle inline audio player visibility - KEEP FOR REFERENCE
  // const toggleInlineAudioPlayer = (messageContent) => {
  //   setInlineAudioPlayers(prev => {
  //     const newMap = new Map(prev);
  //     newMap.set(messageContent, !newMap.get(messageContent));
  //     return newMap;
  //   });
  // };

  // Add a handler for whisper transcription complete
  const handleWhisperTranscriptionComplete = (transcription, audioId) => {
    // Set the user input to the transcription
    setUserInput(transcription);

    // Store the audio ID for later use when sending the message
    // You may need to add this state variable
    setCurrentAudioId(audioId);
  };

  // Add a handler for sending whisper transcription
  const handleWhisperTranscriptionSend = (transcription, audioId) => {
    // First set the user input
    setUserInput(transcription);

    // Store the audio ID
    setCurrentAudioId(audioId);

    // Create an event object for handleSendMessage
    const event = { preventDefault: () => {} };

    // Call the existing send message function with the transcription
    handleSendMessage(event, transcription, audioId);
  };

  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

   const {
     useWhisperApi,
       speechRecognitionEnabled,
    ttsVoiceGender,
    ttsVoiceQualityLearning,
    autoPlayAudio,
       autoSendSpeech,useGoogleSpeechApi
  } = useSettings();

  // Function to handle receiving transcript from speech recognition
  const handleTranscriptReceived = (transcript) => {
    setUserInput(transcript);
  };

  // Function to handle sending transcript directly from speech recognition
  const handleTranscriptSend = (transcript) => {
    // Set the user input first
    setUserInput(transcript);

    // Then trigger the send
    const event = { preventDefault: () => {} };
    handleSendMessage(event, transcript);
  };

  // Add state for audio
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioJobs, setAudioJobs] = useState({});
  const audioRef = useRef(new Audio());

  const [audioReadyMap, setAudioReadyMap] = useState({});

   const { audioPlayerState, openAudioPlayer, closeAudioPlayer } = useAudioPlayer();

  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [activeFeedback, setActiveFeedback] = useState(null);

  const handleListeningStateChange = (isListening) => {
    setMicrophoneActive(isListening);
  };

  const fetchInitialSessionRef = useRef(false);
  const fetchSession = async () => {
  // Don’t hydrate more than once per mount
  if (fetchInitialSessionRef.current) return;

  setLoading(true);

  try {
    const response = await apiClient.content.resumeSession(sessionId);

    console.log("Session data received:", response.data);

    if (response.data.error) {
      console.error("Session error:", response.data.error);
      showNotification('Session error: ' + response.data.error, 'error');
      return;
    }

    setSession(response.data);

    // Seed the player's voice from the session (AI character's gender for
    // conversations, the learner's saved choice for stories).
    if (response.data.voice_gender) {
      setVoiceGender(response.data.voice_gender);
    }

    // Prefer transcript when resuming
    const transcript = Array.isArray(response.data.transcript)
      ? response.data.transcript
      : [];

    // ---------- PATH 1: hydrate from transcript (last 6 messages) ----------
    if (!fetchInitialSessionRef.current &&
        conversation.length === 0 &&
        transcript.length > 0) {

      // Sort by order and take last 6 entries
      const sorted = [...transcript].sort(
        (a, b) => (a.order || 0) - (b.order || 0)
      );
      const lastSix = sorted.slice(-6);

      const mappedConversation = lastSix.map((entry) => {
        // Map backend roles to frontend roles
        const role =
          entry.role === 'agent'
            ? 'assistant'
            : entry.role === 'user'
            ? 'user'
            : entry.role || 'assistant';

        let content = entry.message || '';
        let options = entry.response_options || [];
        const translation = entry.translation || '';

        // Assistant messages may be JSON string with { response, options, ... }
        if (role === 'assistant' && content) {
          try {
            const parsed = JSON.parse(content);
            if (parsed && typeof parsed === 'object') {
              if (parsed.response) {
                content = parsed.response;
              }
              if (Array.isArray(parsed.options)) {
                options = parsed.options;
              }
            }
          } catch (e) {
            // Not JSON; leave content as-is
          }
        }

        return {
          role,
          content,
          translation,
          options
        };
      });

      // IMPORTANT: replace, don’t append
      setConversation(mappedConversation);

      // Use translation from last assistant message if present
      const lastAssistant = [...mappedConversation]
        .reverse()
        .find((m) => m.role === 'assistant');

      if (lastAssistant?.translation) {
        const cleanTranslation = unescapeHTML(lastAssistant.translation);
        setCurrentTranslation(cleanTranslation);
      }

      // Optionally trigger audio for last assistant message
      if (
        lastAssistant?.content &&
        response.data.target_language &&
        response.data.session_id
      ) {
        const jobId = await requestAudio(
          lastAssistant.content,
          response.data.target_language,
          response.data.session_id
        );
        if (jobId) {
          setAudioJobs((prev) => ({
            ...prev,
            [lastAssistant.content]: jobId
          }));
          setAudioReadyMap((prev) => ({
            ...prev,
            [lastAssistant.content]: false
          }));
          pollAudioStatus(jobId, lastAssistant.content);
        }
      }

    // ---------- PATH 2: legacy sessions (no transcript) ----------
    } else if (
      !fetchInitialSessionRef.current &&
      conversation.length === 0 &&
      transcript.length === 0 &&
      (response.data.initial_content || response.data.last_content)
    ) {
      const initialContent =
        response.data.initial_content || response.data.last_content;
      console.log("Setting initial content:", initialContent);

      let content = initialContent;
      let responseOptions = [];

      try {
        const jsonResponse = JSON.parse(initialContent);
        if (jsonResponse && jsonResponse.response) {
          content = jsonResponse.response;
          if (Array.isArray(jsonResponse.options)) {
            responseOptions = jsonResponse.options;
          }
        }
      } catch (error) {
        console.log(
          "Initial response is not in JSON format, using as plain text"
        );
      }

      setConversation([
        {
          role: 'assistant',
          content,
          translation: response.data.translation || '',
          options: responseOptions
        }
      ]);

      // Request audio for initial message
      if (content && response.data.target_language && response.data.session_id) {
        const jobId = await requestAudio(
          content,
          response.data.target_language,
          response.data.session_id
        );
        if (jobId) {
          setAudioJobs((prev) => ({ ...prev, [content]: jobId }));
          setAudioReadyMap((prev) => ({ ...prev, [content]: false }));
          pollAudioStatus(jobId, content);
        }
      }

      if (response.data.translation) {
        const cleanTranslation = unescapeHTML(response.data.translation);
        setCurrentTranslation(cleanTranslation);
      }

    } else {
      // Either conversation already populated, or nothing useful to hydrate
      console.log(
        "Skipping hydrate: conversation already set or no transcript/content"
      );
    }

    // Fetch vocabulary for this session's language
    if (response.data.target_language) {
      fetchSessionVocabulary(response.data.target_language);
    }

    // Mark as hydrated so we don't run this logic again
    fetchInitialSessionRef.current = true;
  } catch (error) {
    console.error('Full error details:', error);

    if (error.response && error.response.status === 401) {
      showNotification('Your session has expired. Please login again.', 'error');
      setTimeout(() => navigate('/login'), 3000);
    } else {
      showNotification(
        'Error loading session: ' + (error.message || 'Unknown error'),
        'error'
      );
    }
  } finally {
    setLoading(false);
  }
};



  // Fetch session data on mount
  useEffect(() => {
  preloadVoices();
  fetchSession();
}, [sessionId, navigate]);

  // Scroll to bottom when conversation updates
  useEffect(() => {
    scrollToBottom();
  }, [conversation]);

  useEffect(() => {
    return () => {
      // Close the audio player if it's open
      closeAudioPlayer();
    };
  }, [closeAudioPlayer]);

  useEffect(() => {
    return () => {
      // Clean up audio on unmount
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  // Keep the floating tutor in sync with the current session state.
  useEffect(() => {
    if (!session) return;
    const isStory = session.content_type === 'story';

    const recentTurns = (conversation || []).slice(-4).map(m => ({
      speaker: m.role === 'assistant' ? 'Character' : (m.role === 'user' ? 'Learner' : 'System'),
      text: typeof m.content === 'string' ? m.content : ''
    })).filter(t => t.text);

    // Latest suggested replies offered to the learner.
    const lastAssistant = [...(conversation || [])].reverse()
      .find(m => m.role === 'assistant' && Array.isArray(m.options) && m.options.length);
    const options = lastAssistant
      ? lastAssistant.options.map(o => (o && (o.response || o.text)) || (typeof o === 'string' ? o : '')).filter(Boolean)
      : [];

    // Most recent corrections the learner received.
    const lastCorr = [...(conversation || [])].reverse().find(m => m.corrections);
    let corrections = [];
    if (lastCorr && lastCorr.corrections) {
      const arr = Array.isArray(lastCorr.corrections) ? lastCorr.corrections : [lastCorr.corrections];
      corrections = arr
        .map(c => ({ original: c.original, correction: c.correction }))
        .filter(c => c.original || c.correction);
    }

    setTutorContext({
      page: isStory ? 'story' : 'conversation',
      title: session.content_title,
      proficiency_level: session.proficiency_level,
      learner_role: session.learner_role || null,
      ai_role: session.ai_role || null,
      recent_turns: recentTurns,
      options,
      corrections,
    });
  }, [session, conversation, setTutorContext]);

  // Clear tutor context when leaving the session.
  useEffect(() => () => clearTutorContext(), [clearTutorContext]);

  // Fetch vocabulary for the current language
  const fetchSessionVocabulary = async (languageCode) => {
    if (!languageCode) return;

    setLoadingVocabulary(true);
    try {
      // Convert full language names to language codes if needed
      let code = languageCode.toLowerCase();
      if (code.length > 2) {
        // Map common language names to codes
        const languageMap = {
          'french': 'fr',
          'spanish': 'es',
          'german': 'de',
          'italian': 'it',
          'japanese': 'ja',
          'romanian': 'ro',
          'portuguese': 'pt',
          'english': 'en'
        };
        code = languageMap[code] || code.slice(0, 2);
      }

      const response = await apiClient.vocabulary.getVocabularyItems({ language_code: code });
      setSessionVocabulary(response.data.vocabulary || []);
    } catch (error) {
      console.error('Error fetching vocabulary:', error);
    } finally {
      setLoadingVocabulary(false);
    }
  };

  // Change the voice for this session. Already-generated lines keep their audio;
  // lines generated after this use the new voice. Also saved as the user's default.
  const handleVoiceGenderChange = async (newGender) => {
    if (!newGender || newGender === voiceGender) return;
    setVoiceGender(newGender);
    try {
      await apiClient.sessions.setVoiceGender(sessionId, newGender);
      showNotification(`Voice set to ${newGender.toLowerCase()}`, 'success');
    } catch (e) {
      console.error('Failed to update session voice:', e);
      showNotification('Could not update voice', 'error');
    }
  };

  // Function to request audio generation
  const requestAudio = async (text, language, overrideSessionId = null, messageId = null) => {
  try {
    const response = await apiClient.audio.generateAudio({
      text,
      language: language || session?.target_language,
      voice_type: 'STANDARD',
      // Prefer this session's voice (role-based for conversations, learner's
      // choice for stories); fall back to the global setting.
      gender: voiceGender || ttsVoiceGender || 'Female',
      quality: ttsVoiceQualityLearning || 'low',  // Use setting from context
      speaking_rate: 1.0,
      session_id: overrideSessionId || session?.session_id,
      message_id: messageId // Pass the message ID to associate with the audio
    });

    if (response.data && response.data.job_id) {
      console.log("Audio generation requested, job ID:", response.data.job_id);

      // Check if the response indicates that existing audio was reused
      if (response.data.reused) {
        console.log("Reused existing audio with job ID:", response.data.job_id);
        setAudioReadyMap(prev => ({ ...prev, [text]: true }));
      } else {
        // Start polling for audio status for newly generated audio
        pollAudioStatus(response.data.job_id, text);
      }

      // Store job ID with the text
      setAudioJobs(prev => ({
        ...prev,
        [text]: response.data.job_id
      }));

      return response.data.job_id;
    }
  } catch (error) {
    console.error("Error requesting audio:", error);
  }

  return null;
};

  // Function to poll audio status
  const pollAudioStatus = async (jobId, text) => {
    try {
      const checkStatus = async () => {
        const response = await apiClient.audio.getAudioStatus(jobId);

        if (response.data.status === 'completed') {
          setAudioReadyMap(prev => ({ ...prev, [text]: true }));
          console.log("Audio generation completed for job:", jobId);
          return true;
        } else if (response.data.status === 'error') {
          console.error("Audio generation failed:", response.data.message);
          return true;
        }

        return false;
      };

      // Start polling
      const poll = async () => {
        const isComplete = await checkStatus();
        if (!isComplete) {
          setTimeout(poll, 2000); // Check every 2 seconds
        }
      };

      poll();
    } catch (error) {
      console.error("Error polling audio status:", error);
    }
  };

  // Function to play audio
  const playAudio = async (text, messageId = null) => {
    try {
      // Get or request job ID for this text
      let jobId = audioJobs[text];
      if (!jobId) {
        jobId = await requestAudio(text, session?.target_language, null, messageId);
        if (!jobId) return;
      }

      // Get audio URL
      const audioUrl = apiClient.audio.getAudioUrl(jobId);

      // Use our audio player instead of the raw Audio element
      openAudioPlayer(audioUrl, `${session?.content_title} - Audio`, messageId);
      setAudioPlaying(true);

      // The AudioPlayer component will handle playback, events, etc.
    } catch (error) {
      console.error("Error playing audio:", error);
      setAudioPlaying(false);
    }
  };

  // Function to preload voices
  const preloadVoices = () => {
    if (window.speechSynthesis) {
      // Get voices - this may return an empty array initially
      const voices = window.speechSynthesis.getVoices();

      if (voices.length === 0) {
        // If voices aren't loaded yet, set up an event listener
        window.speechSynthesis.onvoiceschanged = () => {
          console.log("Speech synthesis voices loaded:", window.speechSynthesis.getVoices().length);
        };

        // Force initialization to trigger the event
        window.speechSynthesis.getVoices();
      } else {
        console.log("Speech synthesis voices already loaded:", voices.length);
      }
    } else {
      console.warn("Speech synthesis not supported in this browser");
    }
  };

  // Function to play word pronunciation
  const playWordPronunciation = (word, language) => {
    if (!word) return;

    try {
      // Check if speech synthesis is available
      if (!window.speechSynthesis) {
        console.error("Browser does not support speech synthesis");
        showNotification("Your browser doesn't support text-to-speech", "warning");
        return;
      }

      // Stop any current speech
      window.speechSynthesis.cancel();

      // Create a new utterance
      const utterance = new SpeechSynthesisUtterance(word);

      // Set language based on the session language
      // Convert full language names to BCP 47 language tags
      let langCode = language || session?.target_language;
      if (langCode) {
        // Map common language names to BCP 47 codes
        const languageMap = {
          'english': 'en-US',
          'french': 'fr-FR',
          'spanish': 'es-ES',
          'german': 'de-DE',
          'italian': 'it-IT',
          'japanese': 'ja-JP',
          'romanian': 'ro-RO',
          'portuguese': 'pt-PT'
        };

        langCode = languageMap[langCode.toLowerCase()] || langCode;
        utterance.lang = langCode;
      }

      // Set voice properties
      utterance.rate = 0.8; // Slightly slower for language learning
      utterance.pitch = 1.0;

      // Optionally, try to select an appropriate voice
      setTimeout(() => {
        const voices = window.speechSynthesis.getVoices();
        const matchingVoice = voices.find(voice =>
          voice.lang.startsWith(langCode.split('-')[0]) &&
          !voice.name.includes('Google') // Skip Google voices as they may sound robotic
        );

        if (matchingVoice) {
          utterance.voice = matchingVoice;
        }

        // Play the pronunciation
        window.speechSynthesis.speak(utterance);
      }, 100); // Small delay to ensure voices are loaded

    } catch (error) {
      console.error("Error using speech synthesis:", error);
      showNotification("Error playing pronunciation", "error");
    }
  };

  // Send user message
  const handleSendMessage = async (e, selectedOption = null, audioId = null, action = null, actionDetail = null) => {
  e?.preventDefault();

  // If selectedOption is provided, use it instead of the input
  const messageToSend = selectedOption || userInput;

  if (sendingMessage || !sessionId) return;
  // A scene action (#8) needs no typed text; a normal turn does.
  if (!action && (!messageToSend || !messageToSend.trim())) return;

  setSendingMessage(true);

  if (action) {
    // #8: a scene-steering action — show a transition marker, not a user bubble.
    const actionLabel = action === 'custom'
      ? (actionDetail || 'New scene')
      : ((CONVERSATION_ACTIONS.find((a) => a.id === action) || {}).label || 'Continue');
    setConversation(prev => [...prev, { role: 'system', content: actionLabel, sceneChange: action }]);
  } else {
    // Add user message to conversation immediately
    const newUserMessage = {
      role: 'user',
      content: messageToSend,
      // Initialize with empty feedback fields
      corrections: null,
      suggestions: null
    };
    setConversation(prev => [...prev, newUserMessage]);
    // Only clear text input if not using a selected option
    if (!selectedOption) {
      setUserInput('');
    }
  }

  try {
    const response = await apiClient.content.continueSession({
      session_id: sessionId,
      user_input: action ? '' : messageToSend,
      input_type: 'text',
      audio_id: action ? null : (audioId || currentAudioId), // Use provided audioId or the stored one
      action: action || null,
      action_detail: action ? actionDetail : null
    });

    console.log(response);

     setCurrentAudioId(null);

    // Handle JSON response for both the content and option buttons
    let nextContent = response.data.next_content;
    let responseOptions = [];
    let corrections = null;
    let suggestions = null;

    // Check if next_content is actually a JSON string with response and options
    try {
      // Try to parse as JSON - this will be the new format with options
      const jsonResponse = JSON.parse(nextContent);
      if (jsonResponse && jsonResponse.response) {
        nextContent = jsonResponse.response;

        // Extract options if available
        if (jsonResponse.options && Array.isArray(jsonResponse.options)) {
          responseOptions = jsonResponse.options;
        }

        // Extract corrections if available - handle both array and object format
        if (jsonResponse.corrections) {
          corrections = jsonResponse.corrections;
          console.log("Found corrections:", corrections);
        }

        // Extract suggestions if available
        if (jsonResponse.suggestions) {
          suggestions = jsonResponse.suggestions;
          console.log("Found suggestions:", suggestions);
        }

        if (responseOptions.length > 0) {
          console.log("Found response options:", responseOptions);
        }
      }
    } catch (error) {
      // Not JSON, use as is (legacy format)
      console.log("Response is not in JSON format, using as plain text");
    }

    // If there are corrections or suggestions, update the user message that was just added
    if (corrections || suggestions) {
      setConversation(prev => {
        const updated = [...prev];
        // Find the last user message (should be the second-to-last message)
        const userMessageIndex = updated.length - 1;
        if (userMessageIndex >= 0 && updated[userMessageIndex].role === 'user') {
          updated[userMessageIndex] = {
            ...updated[userMessageIndex],
            corrections,
            suggestions
          };
        }
        return updated;
      });
    }

    // Add assistant response to conversation
    const messageId = `msg_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
    const newAssistantMessage = {
      role: 'assistant',
      content: nextContent,
      translation: response.data.translation || '',
      options: responseOptions,
      message_id: messageId,
      // Words the learner may want to save (no longer auto-added).
      suggestedWords: response.data.suggested_words || []
    };

    setConversation(prev => [...prev, newAssistantMessage]);

    // Automatically request audio
    const jobId = await requestAudio(nextContent, session?.target_language, null, messageId);
    if (jobId) {
      setAudioJobs(prev => ({ ...prev, [nextContent]: jobId }));
      // If it's reused audio, mark it as ready immediately
      if (response.data.reused) {
        setAudioReadyMap(prev => ({ ...prev, [nextContent]: true }));
      } else {
        setAudioReadyMap(prev => ({ ...prev, [nextContent]: false }));
        pollAudioStatus(jobId, nextContent);
      }
    }

    // Save translation
    if (response.data.translation) {
      // Unescape HTML entities in the translation
      const cleanTranslation = unescapeHTML(response.data.translation);
      setCurrentTranslation(cleanTranslation);
    }

    if (response.data.glossary) {
      setSession(prev => ({
        ...prev,
        glossary: {
          ...prev?.glossary,         // Existing glossary
          ...response.data.glossary  // New glossary (overwrites duplicates)
        }
      }));
    }

    // Refresh vocabulary after message exchange
    if (session?.target_language) {
      fetchSessionVocabulary(session.target_language);
    }

    // Return reply info so the hands-free conversation-mode loop (#1) can speak it.
    return { text: nextContent, jobId };
  } catch (error) {
    console.error('Error sending message:', error);
    showNotification('Failed to send message', 'error');
    return null;
  } finally {
    setSendingMessage(false);
  }
};

  const handleOpenFeedback = (message) => {
  if (message.suggestions) {
    setActiveFeedback(message);
    setFeedbackDialogOpen(true);
  }
};

// Add a handler for selecting response options
const handleSelectOption = (optionText) => {
  // Instead of auto-sending, just fill the input field
  setUserInput(optionText);
};

// #8: Actions menu router — scene steering, difficulty, or a custom scenario.
const handleSelectAction = (actionId) => {
  if (actionId === 'make_easier') { handleAdjustDifficulty('lower'); return; }
  if (actionId === 'make_harder') { handleAdjustDifficulty('higher'); return; }
  if (actionId === 'custom') { setCustomSceneText(''); setCustomSceneOpen(true); return; }
  // Scene-steering action (move_on / switch_scene / change_topic / wrap_up / ask_help).
  handleSendMessage(null, null, null, actionId);
};

// "Other — set the scene": send the free-text scenario as a custom scene action.
const submitCustomScene = () => {
  const text = customSceneText.trim();
  setCustomSceneOpen(false);
  if (!text) return;
  handleSendMessage(null, null, null, 'custom', text);
};

// --- Hands-free conversation mode (#1) loop controller ---
// Declared as hoisted functions so the useWhisperRecorder call above can
// reference onRecorderResult/beginListening. State machine:
//   speaking (TTS) -> listening (mic) -> thinking (LLM) -> speaking -> ...
// We own a single, gesture-unlocked <audio> element and detect "ended"
// ourselves (no existing player exposes that callback).

function enterConversationMode() {
  conversationModeRef.current = true;
  setConversationMode(true);
  setCmCurrentText('');
  // Unlock audio within this user gesture so later programmatic play() works (iOS),
  // and unlock the mic level meter used for silence detection.
  cmRecorder.primeAudio();
  try {
    if (!conversationAudioRef.current) conversationAudioRef.current = new Audio();
    conversationAudioRef.current.muted = true;
    conversationAudioRef.current.src = SILENT_WAV;
    const p = conversationAudioRef.current.play();
    if (p && p.catch) p.catch(() => {});
  } catch (e) { /* noop */ }
  // Start by listening (the opening AI line is already visible/heard in the
  // standard view; the first spoken reply follows the learner's first turn).
  beginListening();
}

function exitConversationMode() {
  conversationModeRef.current = false;
  setConversationMode(false);
  setLoopState('idle');
  setCmCurrentText('');
  setCmIsPlaying(false);
  try { if (conversationAudioRef.current) conversationAudioRef.current.pause(); } catch (e) { /* noop */ }
  try { cmRecorder.stopRecording({ discard: true }); } catch (e) { /* noop */ }
}

function beginListening() {
  if (!conversationModeRef.current) return;
  setLoopState('listening');
  cmRecorder.startRecording();
}

// Mic was paused (no speech / error) — the learner taps to talk again.
function resumeListening() {
  if (!conversationModeRef.current) return;
  cmRecorder.primeAudio();
  beginListening();
}

// The recorder ended the turn on its own (see CM_* limits above).
function handleRecorderAutoStop(reason) {
  if (!conversationModeRef.current) return;
  if (reason === 'no_speech') setLoopState('paused');   // recording discarded, mic off
  else setLoopState('thinking');                        // transcription -> onRecorderResult
}

function handleDoneSpeaking() {
  if (!conversationModeRef.current) return;
  // Stop the mic; transcription resolves via onRecorderResult.
  setLoopState('thinking');
  cmRecorder.stopRecording();
}

function speakReply(url, text) {
  if (!conversationModeRef.current) return;
  if (!conversationAudioRef.current) conversationAudioRef.current = new Audio();
  const audio = conversationAudioRef.current;
  setCmCurrentText(text || '');
  setLoopState('speaking');
  try {
    audio.muted = false;
    audio.src = url;
    audio.playbackRate = cmRateRef.current || 1;
    audio.onended = () => beginListening();
    audio.onplay = () => setCmIsPlaying(true);
    audio.onpause = () => setCmIsPlaying(false);
    const p = audio.play();
    if (p && p.catch) p.catch((err) => { console.error('call-mode play failed', err); beginListening(); });
  } catch (e) {
    console.error('call-mode play error', e);
    beginListening();
  }
}

// Audio controls surfaced in the call-mode panel (#1).
function cmTogglePlay() {
  const audio = conversationAudioRef.current;
  if (!audio) return;
  if (audio.paused) { const p = audio.play(); if (p && p.catch) p.catch(() => {}); }
  else { audio.pause(); }
}
function cmSetRate(rate) {
  cmRateRef.current = rate;
  setCmRate(rate);
  if (conversationAudioRef.current) conversationAudioRef.current.playbackRate = rate;
}

function waitForAudioReady(jobId, maxTries = 20) {
  return new Promise((resolve) => {
    let tries = 0;
    const check = async () => {
      if (!conversationModeRef.current) return resolve(false);
      try {
        const r = await apiClient.audio.getAudioStatus(jobId);
        if (r.data.status === 'completed') return resolve(true);
        if (r.data.status === 'error') return resolve(false);
      } catch (e) { /* keep polling */ }
      tries += 1;
      if (tries >= maxTries) return resolve(false);
      setTimeout(check, 1500);
    };
    check();
  });
}

async function onRecorderResult(text, audioId) {
  if (!conversationModeRef.current) return;
  if (!text || !text.trim()) { beginListening(); return; }  // nothing heard — listen again
  setLoopState('thinking');
  let result = null;
  try {
    result = await handleSendMessage(null, text, audioId);
  } catch (e) {
    console.error('conversation-mode send failed', e);
  }
  if (!conversationModeRef.current) return;
  const replyText = result && result.text;
  const jobId = result && result.jobId;
  if (!replyText || !jobId) { beginListening(); return; }  // no audio to speak — continue listening
  const ready = await waitForAudioReady(jobId);
  if (!conversationModeRef.current) return;
  if (ready) speakReply(apiClient.audio.getAudioUrl(jobId), replyText);
  else beginListening();
}

  // Adjust difficulty
  const handleAdjustDifficulty = async (direction) => {
    setSendingMessage(true);

    const adjustmentMessage = direction === 'lower'
      ? "This content is too difficult for me. Please make it easier and simpler."
      : "This content is too easy for me. Please make it more challenging.";

    // Construct a system message for the API
    const systemNote = `[System Note: User finds the content ${direction === 'lower' ? 'too difficult' : 'too easy'}. 
    Please adjust future responses to be ${direction === 'lower' ? 'simpler with easier vocabulary and grammar' : 'more challenging with richer vocabulary and more complex grammar'}.
    Follow user's level request but maintain the current story/conversation context.]`;

    try {
      const response = await apiClient.content.continueSession({
        session_id: sessionId,
        user_input: adjustmentMessage,
        input_type: 'text',
        system_note: systemNote
      });

      // Add adjustment notice
      const adjustmentNotice = {
        role: 'system',
        content: direction === 'lower'
          ? "I'll navigate to calmer waters with simpler language going forward."
          : "I'll chart a more challenging course with advanced language going forward."
      };
      setConversation(prev => [...prev, adjustmentNotice]);
      let nextContent = response.data.next_content;
      let responseOptions = [];

      console.log(nextContent);
      console.log('RESPONSE::::');
      console.log(response);

      // Check if next_content is actually a JSON string with response and options
      try {
        // Try to parse as JSON - this will be the new format with options
        const jsonResponse = JSON.parse(response.data.next_content);
        if (jsonResponse && jsonResponse.response && jsonResponse.options) {
          nextContent = jsonResponse.response;
          responseOptions = jsonResponse.options;
          console.log("Found response options:", responseOptions);
        }
      } catch (error) {
        // Not JSON, use as is (legacy format)
        console.log("Response is not in JSON format, using as plain text");
      }

      // Add assistant response to conversation
      const newAssistantMessage = {
        role: 'assistant',
        content: nextContent,
        translation: response.data.translation || '',
        options: responseOptions,
        suggestedWords: response.data.suggested_words || []
      };

      setConversation(prev => [...prev, newAssistantMessage]);

      // Save translation
      if (response.data.translation) {
        setCurrentTranslation(response.data.translation);
      }

      if (response.data.glossary) {
        setSession(prev => ({
          ...prev,
          glossary: {
            ...prev?.glossary,         // Existing glossary
            ...response.data.glossary  // New glossary (overwrites duplicates)
          }
        }));
      }

      if(response.data.corrections){
        console.log(response.data.corrections);
        //TODO: Render a feeedback button on the user message and show corrections/suggestions
      }

      if(response.data.suggestions){
        console.log(response.data.suggestions)
        //TODO: Render a feedback button on the user message and show corrections/suggestions
      }

      showNotification(
        direction === 'lower'
          ? 'Content difficulty reduced'
          : 'Content difficulty increased',
        'success'
      );

      // Refresh vocabulary after message exchange
      if (session?.target_language) {
        fetchSessionVocabulary(session.target_language);
      }

    } catch (error) {
      console.error('Error adjusting difficulty:', error);
      showNotification('Failed to adjust difficulty', 'error');
    } finally {
      setSendingMessage(false);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    setUserInput(e.target.value);
  };

  // Toggle translation
  const handleToggleTranslation = () => {
    setShowTranslation(!showTranslation);
  };

  // Toggle vocabulary drawer
  const handleToggleVocabDrawer = () => {
    setVocabDrawerOpen(!vocabDrawerOpen);
  };

  // Open word dialog
  const handleOpenWordDialog = (wordData) => {
    console.log("Word data passed to handleOpenWordDialog:", wordData);

    // Handle different types of input
    if (typeof wordData === 'string') {
      // If it's a string, create a word object
      const cleanWord = wordData.replace(/^[.,;:!?'"()[\]{}]+|[.,;:!?'"()[\]{}]+$/g, '').trim();

      // Get translation from glossary
      const translation = getTranslationForWord(cleanWord);

      setSelectedWord({
        word: cleanWord,
        translation: translation || '',
        language_code: session?.target_language,
        knowledge_level: 1,
        tags: []
      });
    } else if (typeof wordData === 'object' && wordData !== null) {
      // If it's already a word object, use it directly
      const cleanWord = wordData.word.replace(/^[.,;:!?'"()[\]{}]+|[.,;:!?'"()[\]{}]+$/g, '').trim();
      const translation = getTranslationForWord(cleanWord);

      wordData.translation = translation || wordData.translation;
      wordData.word = cleanWord;
      setSelectedWord(wordData);
    } else {
      console.error("Invalid word data:", wordData);
      return;
    }

    setWordDialogOpen(true);
  };

  const getTranslationForWord = (word) => {
    console.log('word:  ' + word + typeof word);
    if (!session?.glossary) return null;

    // Try exact match first
    if (session.glossary[word]) {
      return session.glossary[word];
    }

    // Try case-insensitive match
    const lowerCaseWord = word.toLowerCase();
    const keys = Object.keys(session.glossary);

    for (const key of keys) {
      if (key.toLowerCase() === lowerCaseWord) {
        return session.glossary[key];
      }
    }

    return null;
  };

  // Close word dialog
  const handleCloseWordDialog = () => {
    setWordDialogOpen(false);
  };

  const handleSaveWord = async (wordData) => {
  try {
    if (wordData.id) {
      // Update existing word
      await apiClient.vocabulary.updateVocabularyItem(
        wordData.id,
        {
          translation: wordData.translation,
          context: wordData.context,
          knowledge_level: wordData.knowledge_level,
          tags: wordData.tags // Added tags support
        }
      );

      // Update local state
      setSessionVocabulary(prev =>
        prev.map(item =>
          item.id === wordData.id
            ? { ...item, ...wordData }
            : item
        )
      );

      showNotification('Word updated successfully', 'success');
    } else {
      // Add new word
      const response = await apiClient.vocabulary.addVocabularyItem({
        word: wordData.word,
        translation: wordData.translation,
        context: wordData.context,
        knowledge_level: wordData.knowledge_level,
        language_code: wordData.language_code,
        tags: wordData.tags // Added tags support
      });

      // Add to local vocabulary state
      setSessionVocabulary(prev => [...prev, response.data.vocabulary_item]);

      showNotification('Word added to vocabulary', 'success');
    }
  } catch (error) {
    console.error('Error saving vocabulary word:', error);

    if (error.response?.data?.error?.includes('already in your vocabulary')) {
      showNotification('This word is already in your vocabulary', 'info');
    } else {
      showNotification('Failed to save vocabulary word', 'error');
    }
  }
};

  // Update word knowledge level
  const handleUpdateWordKnowledge = async (wordId, level) => {
    try {
      await apiClient.vocabulary.updateVocabularyItem(wordId, { knowledge_level: level });

      // Update local state
      setSessionVocabulary(prev =>
        prev.map(item =>
          item.id === wordId ? { ...item, knowledge_level: level } : item
        )
      );

    } catch (error) {
      console.error('Error updating knowledge level:', error);
      showNotification('Failed to update word knowledge level', 'error');
    }
  };

  // Navigate back to explore page
  const handleBackToExplore = () => {
    navigate('/explore');
  };

  // Show notification
  const showNotification = (message, severity = 'info') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // Close notification
  const handleCloseNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };

  // Scroll to bottom of conversation
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Word tap router: in call mode show a quick translation bubble (less
  // disruptive while listening); otherwise open the full word dialog.
  const handleWordClick = (e, wordData, rawWord) => {
    if (conversationMode) {
      const translation = (wordData && wordData.translation)
        || (session?.glossary && (session.glossary[wordData?.word] || session.glossary[rawWord]))
        || '';
      setQuickTrans({ anchorEl: e.currentTarget, word: (wordData && wordData.word) || rawWord, translation });
      return;
    }
    handleOpenWordDialog(wordData);
  };

  // Format message with clickable vocabulary words
  const formatMessage = (text, isAssistant) => {
    if (!text) return '';

    // Simple formatting for non-assistant messages
    if (!isAssistant) {
      return text.split('\n').map((line, i) => (
        <React.Fragment key={i}>
          {line}
          <br />
        </React.Fragment>
      ));
    }

    // For assistant messages, make vocabulary words clickable
    // Improved regex to properly handle accents, apostrophes and other special characters
    // This regex will match words including accented characters, apostrophes, and hyphens
    const words = text.split(/(\s+|['']|[.,;:!?"()[\]{}](?:\s|$))/);

    return words.map((word, index) => {
      // Skip empty words, spaces, and lone punctuation
      if (!word.trim() || /^[\s.,;:!?'"()[\]{}]+$/.test(word)) {
        return word;
      }

      // Clean the word to check against vocabulary - preserve accents but remove punctuation at edges
      // We keep hyphens inside words but remove apostrophes
      const cleanWord = word.trim().replace(/^[.,;:!?'"()[\]{}]+|[.,;:!?'"()[\]{}]+$/g, '').replace(/['']/, '');

      if (!cleanWord) return word;

      // Check if the word is in our vocabulary (case-insensitive)
      const vocabWord = sessionVocabulary.find(v =>
        v.word.toLowerCase() === cleanWord.toLowerCase()
      );

      if (vocabWord) {
        // Return styled word based on knowledge level
        const knowledgeColor = getKnowledgeLevelColor(vocabWord.knowledge_level);
        return (
          <Tooltip key={index} title={vocabWord.translation}>
            <Box
              component="span"
              onClick={(e) => handleWordClick(e, vocabWord, word)}
              sx={{
                cursor: 'pointer',
                color: `${knowledgeColor}.main`,
                fontWeight: 'medium',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {word}
            </Box>
          </Tooltip>
        );
      }

      // For words not in vocabulary, make them clickable to add
      return (
        <Tooltip key={index} title="Click to add to vocabulary">
          <Box
            component="span"
            onClick={(e) => handleWordClick(e, {
              word: cleanWord,
              translation: '',
              language_code: session?.target_language,
              knowledge_level: 1
            }, word)}
            sx={{
              cursor: 'pointer',
              color: 'text.primary',
              borderBottom: '1px dotted rgba(10, 49, 97, 0.3)', // Light dotted underline with primary color
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                color: 'primary.main',
                borderBottom: '1px dotted #0A3161' // Darker dotted underline on hover
              }
            }}
          >
            {word}
          </Box>
        </Tooltip>
      );
    });
  };

  const [translationModalOpen, setTranslationModalOpen] = useState(false);
  const [activeTranslation, setActiveTranslation] = useState('');

  // Create a function to handle opening the translation modal
  const handleOpenTranslationModal = (translation) => {
    if (!translation) return;

    // Unescape HTML entities in the translation if needed
    const cleanTranslation = unescapeHTML(translation);
    setActiveTranslation(cleanTranslation);
    setTranslationModalOpen(true);
  };

  // Close the translation modal
  const handleCloseTranslationModal = () => {
    setTranslationModalOpen(false);
  };

  // Get color based on knowledge level
  const getKnowledgeLevelColor = (level) => {
    switch (level) {
      case 1:
        return 'error';
      case 2:
        return 'warning';
      case 3:
        return 'success';
      default:
        return 'primary';
    }
  };

  const unescapeHTML = (html) => {
    if (!html) return '';

    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.documentElement.textContent;
  };

   const renderMessages = () => {
    // "New words" suggestions show under the latest AI reply only.
    let lastAssistant = -1;
    conversation.forEach((m, i) => { if (m.role === 'assistant') lastAssistant = i; });
    return conversation.map((message, index) => (
      <React.Fragment key={index}>
        <MessageBubble
          message={message}
          isUser={message.role === 'user'}
          isSystem={message.role === 'system'}
          // Pass handlers
          onOpenWordDialog={handleOpenWordDialog}
          onFeedback={(m) => { setActiveFeedback(m); setFeedbackDialogOpen(true); }}
          // Pass audio props
          getAudioUrl={getAudioUrl}
          audioReady={audioReadyMap[message.content]}
          // Pass formatting function
          formatMessage={formatMessage}
        />
        {index === lastAssistant && !conversationMode && message.suggestedWords?.length > 0
          && !dismissedSuggestions[message.message_id || index] && (
          <WordSuggestions
            key={message.message_id || index}
            words={message.suggestedWords}
            languageCode={session?.target_language}
            onAdded={() => session?.target_language && fetchSessionVocabulary(session.target_language)}
            onDismiss={() => setDismissedSuggestions((d) => ({ ...d, [message.message_id || index]: true }))}
          />
        )}
      </React.Fragment>
    ));
  };

   const renderSuggestions = () => {
    const lastMessage = conversation[conversation.length - 1];

    // Only show if last message is assistant and has options
    if (!lastMessage || lastMessage.role !== 'assistant' || !lastMessage.options?.length) {
      return null;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          py: 1,
          px: 2,
          // Hide scrollbar but keep functionality
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        {lastMessage.options.map((option, index) => (
          <Chip
            key={index}
            label={option.response || option} // Handle object or string
            onClick={() => handleSelectOption(option.response || option)}
            color="primary"
            variant="outlined"
            clickable
            sx={{
              borderColor: theme.palette.primary.main,
              fontWeight: 500,
              flexShrink: 0 // Prevent squishing
            }}
          />
        ))}
      </Box>
    );
  };

  const ResponseOptionsSection = ({ options, onSelectOption }) => {
    if (!options || options.length === 0) return null;

    return (
      <Box sx={{ mb: 3, mt: 2 , textDecoration: 'none', textTransform: 'none'}}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>
          Choose a response:
        </Typography>
        <Grid container spacing={2}>
          {options.map((option) => (
            <Grid item xs={12} key={option.number}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                onClick={() => onSelectOption(option.response)}
                sx={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  py: 1.5,
                  borderRadius: 2,
                  whiteSpace: 'normal',
                  height: 'auto'
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Option {option.number}
                  </Typography>
                  <Typography variant="body2">{option.response}</Typography>
                </Box>
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Toggle for the redesigned desktop/iPad conversation layout (bounded flex
  // column with an in-flow input). Set to false to instantly roll back to the
  // previous layout. Mobile is intentionally left on the original layout.
  const NEW_CHAT_LAYOUT = true;

  return (
    <Box sx={{
      position: 'relative',
      mx: isMobile ? -5 : 0, // Remove horizontal margin on mobile
      ...(NEW_CHAT_LAYOUT && !isMobile ? {
        display: 'flex',
        flexDirection: 'column',
        // Fill the viewport minus the app bar, page padding, and the bottom nav
        // (which stays visible on all sizes) so the messages area scrolls
        // internally instead of growing the whole page. Tune 160px if needed.
        height: 'calc(100vh - 160px)',
      } : {})
    }}>
      {/* Session header */}
<Card sx={{
 mb: 3,
 borderRadius: isMobile ? 2 : 1,
}}>
 <CardContent>
   <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
     <Box sx={{ display: 'flex', alignItems: 'center' }}>
       <IconButton onClick={handleBackToExplore} sx={{ mr: 1 }}>
         <ArrowBackIcon />
       </IconButton>
       <Box>
         <Typography variant="h5" sx={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600 }}>
           {session?.content_title}
         </Typography>
         <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
           {/* Language + level are redundant on small screens (the learner set them) — hide to declutter */}
           <Chip
             label={session?.target_language}
             size="small"
             color="primary"
             sx={{ mr: 1, mb: isMobile ? 0.5 : 0, display: { xs: 'none', sm: 'inline-flex' } }}
           />
           <Chip
             label={session?.proficiency_level}
             size="small"
             color="secondary"
             sx={{ mb: isMobile ? 0.5 : 0, display: { xs: 'none', sm: 'inline-flex' } }}
           />
           <Chip
             label={session?.content_type === 'story' ? 'Story' : 'Conversation'}
             size="small"
             variant="outlined"
             sx={{ ml: isMobile ? 0 : 1, mb: isMobile ? 0.5 : 0 }}
             icon={session?.content_type === 'story' ? <MenuBookIcon /> : <QuoteIcon />}
           />
           {/* Voice selector: change the spoken voice (male/female) for this session */}
           <Box sx={{ display: 'inline-flex', alignItems: 'center', ml: isMobile ? 0 : 1, mb: isMobile ? 0.5 : 0 }}>
             <Tooltip title="Voice">
               <RecordVoiceOverIcon fontSize="small" sx={{ mr: 0.5, opacity: 0.6 }} />
             </Tooltip>
             {['Female', 'Male'].map((g) => (
               <Chip
                 key={g}
                 label={g}
                 size="small"
                 onClick={() => handleVoiceGenderChange(g)}
                 color={voiceGender === g ? 'primary' : 'default'}
                 variant={voiceGender === g ? 'filled' : 'outlined'}
                 sx={{ ml: 0.5 }}
               />
             ))}
           </Box>
         </Box>
       </Box>
     </Box>

     <Box>
       <Tooltip title="View Vocabulary">
         <Badge
           badgeContent={sessionVocabulary.length}
           color="primary"
           overlap="circular"
         >
           <IconButton
             onClick={handleToggleVocabDrawer}
             color={vocabDrawerOpen ? "primary" : "default"}
           >
             <ListAltIcon />
           </IconButton>
         </Badge>
       </Tooltip>
     </Box>
   </Box>
 </CardContent>
</Card>

{/* Conversation container */}
<Paper
  elevation={2}
  sx={{
    p: isMobile ? 1 : 2,
    height: 'auto',
    //pb : '100px',
    // Use flex-grow instead of fixed height to fill available space
    flex: "1 1 auto",
    minHeight: 0, // allow the flex child to shrink so overflowY can scroll
    // Enable scrolling for mobile and desktop
    overflowY: "auto",
    // Remove fixed margin that causes issues with keyboard
    mb: 1,
    bgcolor: 'grey.50',
    borderRadius: isMobile ? 2 : undefined,
    // Ensure content starts from the bottom when there's little content
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end"
  }}
>
  <Box
    sx={{
      flexGrow: 1,
      overflowY: 'auto',
      padding: 2,
      paddingBottom: isMobile ? '210px' : (NEW_CHAT_LAYOUT ? 2 : '1000px'), // space for input + nav bar
    }}
  >
    {renderMessages()}
    <div ref={messagesEndRef} />
  </Box>

</Paper>
<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover"/>
{/* Input form */}
<Box
  sx={
    (NEW_CHAT_LAYOUT && !isMobile) ? {
      // In-flow input: lives at the bottom of the conversation column, so it
      // aligns with the messages above it and ignores the side drawer offset.
      position: 'static',
      width: '100%',
      flexShrink: 0,
      zIndex: 100,
      backgroundColor: 'background.paper',
      borderTop: '1px solid #ccc',
      px: 2,
      py: 1,
    } : {
      position: 'fixed',
      bottom: 'var(--bottom-nav-height, 0px)', // sit on top of the bottom nav at its real height (set by Layout)
      left: isMobile ? 0 : 'auto',
      right: isMobile ? 0 : 'auto',
      width: isMobile ? '100%' : 'auto',
      maxWidth: isMobile ? '100%' : 'inherit',
      zIndex: 100,
      backgroundColor: 'background.paper',
      borderTop: '1px solid #ccc',
      px: 2,
      py: 1,
      // Center the input box and match the conversation width on desktop
      ...(isMobile ? {} : {
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'var(--conversation-width, 90%)',
        maxWidth: 'var(--conversation-max-width, 1200px)',
        pb: '60px'
      })
    }
  }
>
  {/* Input form */}
  {/* Input form */}
<Box
  component="form"
  onSubmit={handleSendMessage}
  sx={{
    position: 'relative',
    mx: isMobile ? 1 : 0
  }}
>
  {!conversationMode && (
  <Grid container spacing={1}>
    <Grid item xs={12}>
      <TextField
        fullWidth
        multiline
        rows={isMobile ? 2 : 3}
        variant="outlined"
        placeholder={microphoneActive || whisperRecording ? "Listening..." : "Type your response here..."}
        value={userInput}
        onChange={handleInputChange}
        disabled={sendingMessage || microphoneActive || whisperRecording}
        onFocus={() => {
          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 300);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
          }
        }}
        sx={{
          backgroundColor: 'white',
          '& .MuiOutlinedInput-root': {
            paddingRight: '80px' // Extra space for the stacked buttons
          }
        }}
        InputProps={{
          endAdornment: (
            <Box sx={{
              position: 'absolute',
              right: 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              paddingY: 0
            }}>
              {/* Hidden settings button */}
              {/* <SpeechSettingsButton sx={{ display: 'none' }} /> */}

              {/* Speech input - conditional rendering based on settings */}
              {useWhisperApi ? (
                // Use Whisper Recording component
                <WhisperRecording
                  sessionId={sessionId}
                  targetLanguage={session?.target_language}
                  onTranscriptionComplete={handleWhisperTranscriptionComplete}
                  onSend={handleWhisperTranscriptionSend}
                  onRecordingStateChange={handleWhisperRecordingChange}
                />
              ) : speechRecognitionEnabled ? (
                // Use existing Web Speech API component
                <InlineSpeechRecognition
                  targetLanguage={session?.target_language}
                  useGoogleApi={useGoogleSpeechApi}
                  onTranscriptChange={(text) => setUserInput(text)}
                  inputValue={userInput}
                  autoSend={autoSendSpeech}
                  onSend={handleSendMessage}
                  sessionId={sessionId}
                  onListeningChange={handleListeningStateChange}
                />
              ) : null}

              {/* Send button at the bottom */}
              <IconButton
                color="primary"
                onClick={(e) => handleSendMessage(e)}
                disabled={!userInput.trim() || sendingMessage}
                aria-label="send"
                sx={{
                  position: 'relative',
                  bottom: 0,
                  backgroundColor: 'white',
                  color: 'primary.main',
                  height: 40,
                  borderRadius: '20px',
                  width: 40,
                  '&:hover': {
                    color: 'primary.dark',
                  },
                  '&.Mui-disabled': {
                    color: 'grey.300',
                  }
                }}
              >
                {sendingMessage ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
              </IconButton>
            </Box>
          ),
        }}
      />
    </Grid>
  </Grid>
  )}

  {/* #1: call-mode panel takes over the input box while a call is active */}
  {conversationMode && (
    <ConversationModeOverlay
      variant={session?.content_type === 'story' ? 'story' : 'call'}
      loopState={loopState}
      currentText={cmCurrentText}
      onTapDone={handleDoneSpeaking}
      onResume={resumeListening}
      secondsLeft={cmRecorder.secondsLeft}
      onExit={exitConversationMode}
      isPlaying={cmIsPlaying}
      onTogglePlay={cmTogglePlay}
      playbackRate={cmRate}
      onSetRate={cmSetRate}
    />
  )}

  {/* Quick translation bubble (word tap in call mode) */}
  <Popover
    open={Boolean(quickTrans)}
    anchorEl={quickTrans?.anchorEl}
    onClose={() => setQuickTrans(null)}
    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    PaperProps={{ sx: { p: 1.25, maxWidth: 260 } }}
    disableRestoreFocus
  >
    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{quickTrans?.word}</Typography>
    <Typography variant="body2" color="text.secondary">
      {quickTrans?.translation || 'No translation yet — exit call mode to add it.'}
    </Typography>
  </Popover>

 {/* Add the AudioPlayer component at the end
      <AudioPlayer
        audioSrc={audioPlayerState.audioSrc}
        title={audioPlayerState.title}
        onClose={() => {
          closeAudioPlayer();
          setAudioPlaying(false);
        }}
        autoPlay={true}
        position="bottom"
      />*/}

  {/* Suggestions + the two control buttons — hidden while in call mode */}
  {!conversationMode && (
    <>
      {conversation.length > 0 &&
        conversation[conversation.length - 1].role === 'assistant' &&
        conversation[conversation.length - 1].options &&
        conversation[conversation.length - 1].options.length > 0 && (
          <ResponseSuggestions
            options={conversation[conversation.length - 1].options}
            onSelectOption={handleSelectOption}
          />
      )}

      {/* Actions menu (steer / difficulty / custom) + the voice loop button, on one
          line. Same menu for conversations and stories; the voice loop is called
          Call Mode in conversations and Hands Free in stories. */}
      {conversation.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'nowrap', gap: 1.5, mt: 1.5, mb: isMobile ? 1 : 2 }}>
          <ConversationActions onSelectAction={handleSelectAction} disabled={sendingMessage} />
          <Button
            size="small"
            variant="contained"
            onClick={enterConversationMode}
            disabled={sendingMessage}
            sx={{ textTransform: 'none', borderRadius: 4, whiteSpace: 'nowrap' }}
          >
            {session?.content_type === 'story' ? '🎧 Hands Free' : '📞 Call Mode'}
          </Button>
        </Box>
      )}
    </>
  )}

  {/* #8: "Other — set the scene" dialog */}
  <Dialog open={customSceneOpen} onClose={() => setCustomSceneOpen(false)} fullWidth maxWidth="sm">
    <DialogTitle>Set the scene</DialogTitle>
    <DialogContent>
      <TextField
        autoFocus
        fullWidth
        multiline
        rows={2}
        placeholder="e.g. I'm checking in at a hotel front desk"
        value={customSceneText}
        onChange={(e) => setCustomSceneText(e.target.value)}
        sx={{ mt: 1 }}
      />
    </DialogContent>
    <DialogActions>
      <Button onClick={() => setCustomSceneOpen(false)}>Cancel</Button>
      <Button variant="contained" onClick={submitCustomScene} disabled={!customSceneText.trim()}>
        Start scene
      </Button>
    </DialogActions>
  </Dialog>
</Box>

</Box>

{/* Vocabulary drawer */}
<Drawer
 anchor="right"
 open={vocabDrawerOpen}
 onClose={handleToggleVocabDrawer}
 sx={{
   width: isMobile ? '90%' : 320,
   flexShrink: 0,
   '& .MuiDrawer-paper': {
     width: isMobile ? '90%' : 320,
     boxSizing: 'border-box',
   },
 }}
>
 <Box sx={{ mt:8, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
   <Typography variant="h6">Vocabulary</Typography>
   <IconButton onClick={handleToggleVocabDrawer}>
     <CloseIcon />
   </IconButton>
 </Box>
 <Divider />

 {loadingVocabulary ? (
   <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
     <CircularProgress />
   </Box>
 ) : (
   <>
     {sessionVocabulary.length === 0 ? (
       <Box sx={{ p: 3, textAlign: 'center' }}>
         <Typography variant="body1" gutterBottom>
           No vocabulary items yet
         </Typography>
         <Typography variant="body2" color="text.secondary">
           Click on words in the conversation to add them to your vocabulary
         </Typography>
       </Box>
     ) : (
       <List
         subheader={
           <ListSubheader component="div">
             {session?.target_language} Vocabulary ({sessionVocabulary.length})
           </ListSubheader>
         }
       >
         {/* Group by knowledge level */}
         {knowledgeLevels.map(level => {
           const wordsInLevel = sessionVocabulary.filter(
             word => word.knowledge_level === level.value
           );

           if (wordsInLevel.length === 0) return null;

           return (
             <React.Fragment key={level.value}>
               <ListSubheader sx={{ bgcolor: `${level.color}.light`, color: 'text.primary' }}>
                 <Box sx={{ display: 'flex', alignItems: 'center' }}>
                   {React.cloneElement(level.icon, { fontSize: 'small', sx: { mr: 1 } })}
                   {level.label} ({wordsInLevel.length})
                 </Box>
               </ListSubheader>

               {wordsInLevel.map(word => (
                 <ListItem
                   key={word.id}
                   button
                   onClick={() => handleOpenWordDialog(word)}
                 >
                   <ListItemText
                     primary={word.word}
                     secondary={word.translation}
                   />
                 </ListItem>
               ))}
             </React.Fragment>
           );
         })}
       </List>
     )}
   </>
 )}
</Drawer>

{/* Word dialog */}
<WordDialog
  open={wordDialogOpen}
  onClose={handleCloseWordDialog}
  word={selectedWord}
  onSave={handleSaveWord}
  languageCode={session?.target_language}
  disableAutoFocus={isMobile}
  playPronunciation={playWordPronunciation}
/>

{/* Feedback Dialog */}
    <Dialog
      open={feedbackDialogOpen}
      onClose={() => setFeedbackDialogOpen(false)}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SchoolIcon color="secondary" sx={{ mr: 1 }} />
            <Typography variant="h6">Language Feedback</Typography>
          </Box>
          <IconButton size="small" onClick={() => setFeedbackDialogOpen(false)} edge="end">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {activeFeedback && (
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Your message:
            </Typography>
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.100' }}>
              <Typography variant="body1">
                {activeFeedback.content}
              </Typography>
            </Paper>

            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
              Suggestions for improvement:
            </Typography>

            {typeof activeFeedback.suggestions === 'string' ? (
              // Handle string suggestions
              <Typography variant="body1" paragraph>
                {activeFeedback.suggestions}
              </Typography>
            ) : Array.isArray(activeFeedback.suggestions) ? (
              // Handle array of suggestions - could be array of strings or objects
              <List>
                {activeFeedback.suggestions.map((suggestion, i) => (
                  <ListItem key={i} sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Chip size="small" label={i+1} color="secondary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={typeof suggestion === 'string' ? suggestion : suggestion.suggestion || suggestion.text}
                    />
                  </ListItem>
                ))}
              </List>
            ) : activeFeedback.suggestions && typeof activeFeedback.suggestions === 'object' ? (
              // Handle single suggestion object
              <Typography variant="body1" paragraph>
                {activeFeedback.suggestions.suggestion || activeFeedback.suggestions.text || JSON.stringify(activeFeedback.suggestions)}
              </Typography>
            ) : (
              // Fallback for no suggestions
              <Typography variant="body1" color="text.secondary">
                No specific suggestions available.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setFeedbackDialogOpen(false)} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>

<Dialog
  open={translationModalOpen}
  onClose={handleCloseTranslationModal}
  maxWidth="sm"
  fullWidth
  sx={{
    '& .MuiDialog-paper': {
      borderRadius: 2,
    }
  }}
>
  <DialogTitle>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <TranslateIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h6">Translation</Typography>
      </Box>
      <IconButton size="small" onClick={handleCloseTranslationModal} edge="end">
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  </DialogTitle>
  <DialogContent dividers>
    <Typography variant="body1">
      {activeTranslation}
    </Typography>
  </DialogContent>
</Dialog>

{/* Notification Snackbar */}
<Snackbar
 open={notification.open}
 autoHideDuration={6000}
 onClose={handleCloseNotification}
 anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
>
 <Alert
   onClose={handleCloseNotification}
   severity={notification.severity}
   variant="filled"
   sx={{ width: '100%' }}
 >
   {notification.message}
 </Alert>
</Snackbar>
</Box>
);
};

export default LearningSessionPage;